import { geometricMeanScale, type Matrix } from '../geometry/matrix';
import type { LineCap, LineJoin, StrokeStyle } from '../model/types';

export interface ResolvedStroke {
  readonly color: string;
  readonly widthMm: number;
  readonly opacity: number;
  readonly lineCap: LineCap;
  readonly lineJoin: LineJoin;
}

/**
 * Resolves the physical stroke width for output under an outer transform applied to a
 * whole design (nail placement, print size ladder). `widthMm` is always the width in
 * design space: in-design resizes of `scale-with-design` strokes are folded into
 * `widthMm` by the transform command itself (ADR 0006), so node transforms never
 * change a stroke here.
 *
 * - `fixed-mm`: width is unchanged by any outer transform.
 * - `scale-with-design`: width × sqrt(|det(outer)|), the geometric-mean convention.
 */
export function resolveStrokeWidthMm(stroke: StrokeStyle, outer: Matrix): number {
  return stroke.scaleMode === 'scale-with-design'
    ? stroke.widthMm * geometricMeanScale(outer)
    : stroke.widthMm;
}

export function resolveStroke(stroke: StrokeStyle, outer: Matrix): ResolvedStroke {
  return {
    color: stroke.color,
    widthMm: resolveStrokeWidthMm(stroke, outer),
    opacity: stroke.opacity,
    lineCap: stroke.lineCap,
    lineJoin: stroke.lineJoin,
  };
}

/**
 * Conservative distance a stroke's ink can extend beyond its geometric path.
 * Round caps/joins: w/2. Square caps: w/2·√2. Miter joins are bounded by the SVG
 * default miter limit of 4, so the reach is at most 2w.
 */
export function strokeOutset(stroke: ResolvedStroke): number {
  const half = stroke.widthMm / 2;
  if (stroke.lineJoin === 'miter') return half * 4;
  if (stroke.lineCap === 'square') return half * Math.SQRT2;
  return half;
}
